import { useState, useCallback, useEffect, useRef } from 'react';
import { aiService } from '../services/aiService';

interface Message {
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

interface UseAIFeaturesProps {
  courseId: string;
  videoUrl: string;
}

// Create a cache for mind maps
const mindMapCache: Record<string, string> = {};

// Create a summary cache
const summaryCache: Record<string, {
  videoSummary: string | null;
  sectionSummary: string;
  courseSummary: string;
}> = {};

export const useAIFeatures = ({ courseId, videoUrl }: UseAIFeaturesProps) => {
  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  // Track the current course to detect course changes
  const currentCourseId = useRef<string>(courseId);

  // Transcript state
  const [transcript, setTranscript] = useState<string>('');
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);

  // Summary state
  const [summaries, setSummaries] = useState<{
    videoSummary: string | null;
    sectionSummary: string;
    courseSummary: string;
  }>({
    videoSummary: null,
    sectionSummary: '',
    courseSummary: '',
  });
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [summariesError, setSummariesError] = useState<string | null>(null);

  // Mind map state
  const [mindMap, setMindMap] = useState<string>('');
  const [mindMapLoading, setMindMapLoading] = useState(false);
  const [mindMapError, setMindMapError] = useState<string | null>(null);

  // Add retry tracking for transcript
  const [hasFailedTranscriptFetch, setHasFailedTranscriptFetch] = useState(false);
  const transcriptRetryCount = useRef(0);

  // Add retry tracking for summaries
  const [hasFailedSummariesFetch, setHasFailedSummariesFetch] = useState(false);
  const summariesRetryCount = useRef(0);
  const [summariesFetchAttempted, setSummariesFetchAttempted] = useState(false);

  // Add retry tracking for mind map
  const [hasFailedMindMapFetch, setHasFailedMindMapFetch] = useState(false);
  const mindMapRetryCount = useRef(0);
  const [mindMapFetchAttempted, setMindMapFetchAttempted] = useState(false);

  const MAX_RETRY_ATTEMPTS = 3; // Maximum number of retry attempts

  // Handle course changes - clear chat state when course changes
  useEffect(() => {
    if (currentCourseId.current !== courseId) {
      console.log(`Course changed from ${currentCourseId.current} to ${courseId} - clearing chat state`);
      
      // Clear chat state for course switch
      setMessages([]);
      setThreadId(undefined);
      setChatError(null);
      
      // Update the current course reference
      currentCourseId.current = courseId;
    }
  }, [courseId]);

  // Check if we have a cached mind map for this video when video URL changes
  useEffect(() => {
    const cacheKey = `${courseId}:${videoUrl}`;
    
    // Check mind map cache
    if (mindMapCache[cacheKey]) {
      setMindMap(mindMapCache[cacheKey]);
    } else {
      // Reset mind map if not cached
      setMindMap('');
    }
    
    // Check summary cache
    if (summaryCache[cacheKey]) {
      setSummaries(summaryCache[cacheKey]);
      setSummariesFetchAttempted(true); // Mark as attempted if we have cached data
    }
  }, [courseId, videoUrl]);

  // Reset transcript and summaries state when video URL changes (but not chat for same course)
  useEffect(() => {
    // Don't clear chat history or mind map, only other features
    setTranscript('');
    setSummaries({
      videoSummary: null,
      sectionSummary: '',
      courseSummary: '',
    });
    
    // Reset errors
    setTranscriptError(null);
    setSummariesError(null);
    setMindMapError(null);
    // Note: Don't clear chatError here as it should persist within the same course
    
    // Reset retry tracking
    setHasFailedTranscriptFetch(false);
    transcriptRetryCount.current = 0;
    setHasFailedSummariesFetch(false);
    summariesRetryCount.current = 0;
    setSummariesFetchAttempted(false);
    setHasFailedMindMapFetch(false);
    mindMapRetryCount.current = 0;
    setMindMapFetchAttempted(false);
  }, [videoUrl]);

  // Send message to Adwina
  const sendMessage = useCallback(async (content: string) => {
    try {
      setChatLoading(true);
      setChatError(null);
      
      // Add user message to chat
      const userMessage: Message = {
        content,
        sender: 'user',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, userMessage]);
      
      // Call AI service
      const response = await aiService.chat({
        courseId,
        videoUrl,
        message: content,
        threadId,
      });
      
      // Update thread ID for conversation continuity
      setThreadId(response.threadId);
      
      // Add AI response to chat
      const aiMessage: Message = {
        content: response.response,
        sender: 'ai',
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, aiMessage]);
      return true;
    } catch (err: any) {
      setChatError(err.message || 'Failed to send message. Please try again.');
      console.error('Error in useAIFeatures.sendMessage:', err);
      return false;
    } finally {
      setChatLoading(false);
    }
  }, [courseId, videoUrl, threadId]);

  // Clear chat messages
  const clearMessages = useCallback(() => {
    setMessages([]);
    setThreadId(undefined);
    setChatError(null);
  }, []);

  // Fetch transcript
  const fetchTranscript = useCallback(async () => {
    if (!courseId || !videoUrl) return;
    
    // Skip if already failed and exceeded retry attempts
    if (hasFailedTranscriptFetch && transcriptRetryCount.current >= MAX_RETRY_ATTEMPTS) {
      console.warn(`Skipping transcript fetch after ${MAX_RETRY_ATTEMPTS} failed attempts`);
      return false;
    }
    
    try {
      setTranscriptLoading(true);
      setTranscriptError(null);
      
      const data = await aiService.getTranscript(courseId, videoUrl);
      setTranscript(data);
      
      // Reset failure tracking on success
      setHasFailedTranscriptFetch(false);
      transcriptRetryCount.current = 0;
      
      return true;
    } catch (err: any) {
      // Track failure
      setHasFailedTranscriptFetch(true);
      transcriptRetryCount.current += 1;
      
      const errorMessage = err.message || 'Failed to fetch transcript';
      setTranscriptError(errorMessage);
      console.error(`Error in useAIFeatures.fetchTranscript (attempt ${transcriptRetryCount.current}):`, err);
      
      return false;
    } finally {
      setTranscriptLoading(false);
    }
  }, [courseId, videoUrl, hasFailedTranscriptFetch]);

  // Fetch summaries with retry mechanism
  const fetchSummaries = useCallback(async () => {
    if (!courseId || !videoUrl) return;
    
    // Skip if already failed and exceeded retry attempts
    if (hasFailedSummariesFetch && summariesRetryCount.current >= MAX_RETRY_ATTEMPTS) {
      console.warn(`Skipping summaries fetch after ${MAX_RETRY_ATTEMPTS} failed attempts`);
      return false;
    }
    
    const cacheKey = `${courseId}:${videoUrl}`;
    
    try {
      setSummariesLoading(true);
      setSummariesError(null);
      setSummariesFetchAttempted(true);
      
      const data = await aiService.getSummaries(courseId, videoUrl);
      
      // Store in both state and cache
      setSummaries(data);
      summaryCache[cacheKey] = data;
      
      // Reset failure tracking on success
      setHasFailedSummariesFetch(false);
      summariesRetryCount.current = 0;
      
      return true;
    } catch (err: any) {
      // Track failure
      setHasFailedSummariesFetch(true);
      summariesRetryCount.current += 1;
      
      const errorMessage = err.message || 'Failed to fetch summaries';
      setSummariesError(errorMessage);
      console.error(`Error in useAIFeatures.fetchSummaries (attempt ${summariesRetryCount.current}/${MAX_RETRY_ATTEMPTS}):`, err);
      
      return false;
    } finally {
      setSummariesLoading(false);
    }
  }, [courseId, videoUrl, hasFailedSummariesFetch]);

  // Fetch mind map - now with caching and retry mechanism
  const fetchMindMap = useCallback(async () => {
    if (!courseId || !videoUrl) return;
    
    // Skip if already failed and exceeded retry attempts
    if (hasFailedMindMapFetch && mindMapRetryCount.current >= MAX_RETRY_ATTEMPTS) {
      console.warn(`Skipping mind map fetch after ${MAX_RETRY_ATTEMPTS} failed attempts`);
      return false;
    }
    
    // Return immediately if we already have the mind map cached
    const cacheKey = `${courseId}:${videoUrl}`;
    if (mindMapCache[cacheKey] && mindMap) {
      return true;
    }
    
    try {
      setMindMapLoading(true);
      setMindMapError(null);
      setMindMapFetchAttempted(true);
      
      const data = await aiService.getMindMap(courseId, videoUrl);
      
      // Store in both state and cache
      setMindMap(data);
      mindMapCache[cacheKey] = data;
      
      // Reset failure tracking on success
      setHasFailedMindMapFetch(false);
      mindMapRetryCount.current = 0;
      
      return true;
    } catch (err: any) {
      // Track failure
      setHasFailedMindMapFetch(true);
      mindMapRetryCount.current += 1;
      
      setMindMapError(err.message || 'Failed to fetch mind map');
      console.error(`Error in useAIFeatures.fetchMindMap (attempt ${mindMapRetryCount.current}/${MAX_RETRY_ATTEMPTS}):`, err);
      return false;
    } finally {
      setMindMapLoading(false);
    }
  }, [courseId, videoUrl, mindMap, hasFailedMindMapFetch]);

  // Auto-fetch mind map when courseId or videoUrl changes
  useEffect(() => {
    // Check if we need to fetch the mind map
    if (!mindMap && !mindMapLoading && !mindMapError && 
        mindMapRetryCount.current < MAX_RETRY_ATTEMPTS && 
        !mindMapFetchAttempted) {
      console.log("Auto-fetching mind map for new course/video");
      fetchMindMap();
    }
  }, [courseId, videoUrl, mindMap, mindMapLoading, mindMapError, fetchMindMap, mindMapFetchAttempted]);

  // Generate summary from transcript if video summary is null - with better controls
  useEffect(() => {
    // Only attempt if:
    // 1. We have transcript
    // 2. No video summary exists
    // 3. Not currently loading
    // 4. No error state
    // 5. Haven't exceeded retry attempts
    // 6. Haven't already attempted for this video
    if (transcript && 
        !summaries.videoSummary && 
        !summariesLoading && 
        !summariesError &&
        summariesRetryCount.current < MAX_RETRY_ATTEMPTS &&
        !summariesFetchAttempted) {
      console.log("Attempting to fetch summaries after transcript was loaded");
      fetchSummaries();
    }
  }, [transcript, summaries.videoSummary, summariesLoading, summariesError, summariesFetchAttempted, fetchSummaries]);

  return {
    // Chat
    messages,
    chatLoading,
    chatError,
    sendMessage,
    clearMessages,
    
    // Transcript
    transcript,
    transcriptLoading,
    transcriptError,
    fetchTranscript,
    
    // Summaries
    summaries,
    summariesLoading, 
    summariesError,
    fetchSummaries,
    
    // Mind Map
    mindMap,
    mindMapLoading,
    mindMapError,
    fetchMindMap,
  };
}; 